// Dashboard screen (Screen 1) — PSD §10.2. Cards + new + compliance + trend.

import { FileBarChart } from 'lucide-react';
import { useStore, useTrendData, usePoolCapacity } from '../store/selectors';
import { RoleGate } from '../auth/roleGuard';
import { EmptyState } from '../components/common/EmptyState';
import { LoadDemoButton } from '../components/common/LoadDemoButton';
import { ValuationCard } from '../components/dashboard/ValuationCard';
import { NewValuationButton } from '../components/dashboard/NewValuationButton';
import { RefreshRemindersCard } from '../components/dashboard/RefreshRemindersCard';
import { MaterialEventsCard } from '../components/dashboard/MaterialEventsCard';
import { PoolCapacityCard } from '../components/dashboard/PoolCapacityCard';
import { TrendChart } from '../components/dashboard/TrendChart';

export function Dashboard() {
  const valuations = useStore((s) => Object.values(s.valuations));
  const settings = useStore((s) => s.settings);
  const acknowledgeAlert = useStore((s) => s.acknowledgeAlert);
  const registerMaterialEvent = useStore((s) => s.registerMaterialEvent);

  const trendData = useTrendData(settings.activeEntityName);
  const poolCapacity = usePoolCapacity();

  const entityValuations = settings.activeEntityName
    ? valuations.filter((v) => v.entityName === settings.activeEntityName)
    : valuations;

  const activeId = useStore((s) => s.activeValuationId);
  const allAlerts = useStore((s) => {
    if (activeId) return s.alertsByValuation[activeId] ?? [];
    return Object.values(s.alertsByValuation).flat();
  });

  // Stage 6e /polish: promote the active (or, failing that, most-recent)
  // valuation to a full-width featured card, leaving the rest of the list in
  // a compact auto-fit grid. Breaks the monotony of equally-weighted cards.
  const featuredRecord =
    (activeId && entityValuations.find((v) => v.valuationId === activeId)) ||
    [...entityValuations].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ||
    null;
  const featuredId = featuredRecord?.valuationId ?? null;
  const remainingValuations = entityValuations.filter((v) => v.valuationId !== featuredId);

  return (
    <div>
      <div className="header-row mb-5">
        <h1 className="screen-title">Dashboard</h1>
        <RoleGate allow={['valuation:create']}>
          <NewValuationButton />
        </RoleGate>
      </div>

      <div className="section-group">
        <section>
          <h2 className="section-title">Valuations</h2>
          {entityValuations.length === 0 ? (
            <EmptyState
              icon={<FileBarChart size={32} strokeWidth={1.5} />}
              message="No valuations yet."
              hint="Each valuation here is a defensible common-stock FMV derived from your cap table and latest round — none exist for this entity yet. Load a realistic example to explore the full workflow, or start your own."
              action={
                <>
                  <LoadDemoButton />
                  <RoleGate allow={['valuation:create']}>
                    <NewValuationButton />
                  </RoleGate>
                </>
              }
            />
          ) : (
            <div className="stack-4">
              {featuredRecord && (
                <ValuationCard key={featuredRecord.valuationId} record={featuredRecord} featured />
              )}
              {remainingValuations.length > 0 && (
                <div className="headline-grid">
                  {remainingValuations.map((v) => (
                    <ValuationCard key={v.valuationId} record={v} />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section>
          <h2 className="section-title">Compliance</h2>
          <div className="headline-grid">
            <RefreshRemindersCard
              alerts={allAlerts}
              onAcknowledge={activeId ? (id) => acknowledgeAlert(activeId, id) : undefined}
            />
            <MaterialEventsCard
              alerts={allAlerts}
              onRegister={activeId ? (msg) => registerMaterialEvent(activeId, msg) : undefined}
              onAcknowledge={activeId ? (id) => acknowledgeAlert(activeId, id) : undefined}
            />
            <PoolCapacityCard capacity={poolCapacity} />
          </div>
        </section>

        <section>
          <h2 className="section-title">Trend</h2>
          <TrendChart data={trendData} />
        </section>
      </div>
    </div>
  );
}
